import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import useQueryGet from 'hooks/useQueryGet'
import RequestStatus from 'components/RequestStatus'
import { useAuthContext } from 'context/AuthContext/AuthContext'
import { useAlert } from 'context/AlertContext'
import { axiosPost } from 'connectors/axiosPost'
import { axiosPatch } from 'connectors/axiosPatch'
import { axiosDelete } from 'connectors/axiosDelete'
import type {
  ApplicationField,
  FieldOption,
  FieldSection,
  FieldTranslation,
  FieldType,
  MarketLanguage,
  OptionTranslation,
} from './FormBuilder.types'
import { TrashIcon } from 'assets/icons/trash'
import Add from '@mui/icons-material/Add'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import DoneOutlinedIcon from '@mui/icons-material/DoneOutlined'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'

// ── Types for local (unsaved) state ──────────────────────────────────────────

type LocalOption = Omit<FieldOption, 'id'> & { id?: number }

type LocalField = Omit<ApplicationField, 'id' | 'options'> & {
  id?: number
  options: LocalOption[]
}

// ── Constants ─────────────────────────────────────────────────────────────────

const FIELD_TYPE_LABELS: Record<FieldType, string> = {
  short_text: 'Short text',
  long_text: 'Long text',
  single_select: 'Single select',
  dropdown: 'Dropdown',
  multi_select: 'Multi select',
  file: 'File upload',
  file_text: 'File + description',
}

const HAS_OPTIONS: FieldType[] = ['single_select', 'dropdown', 'multi_select']

const FIELD_SECTION_LABELS: Record<FieldSection, string> = {
  contest: 'Contest',
  address: 'Address',
  info: 'Info',
  custom: 'Custom',
}

function makeEmptyTranslations(languages: MarketLanguage[]): FieldTranslation[] {
  return languages.map(lang => ({ marketLanguageId: lang.id, label: '', placeholder: '', description: '' }))
}

function makeEmptyOptionTranslations(languages: MarketLanguage[]): OptionTranslation[] {
  return languages.map(lang => ({ marketLanguageId: lang.id, label: '' }))
}

// ── FieldCard ────────────────────────────────────────────────────────────────

function FieldCard({
  field,
  index,
  total,
  languages,
  onChange,
  onDelete,
  onMoveUp,
  onMoveDown,
}: {
  field: LocalField
  index: number
  total: number
  languages: MarketLanguage[]
  onChange: (updated: LocalField) => void
  onDelete: () => void
  onMoveUp: () => void
  onMoveDown: () => void
}) {
  const [expanded, setExpanded] = useState(!field.id) // auto-expand new fields
  const [langTab, setLangTab] = useState(0)
  const [newOptionValue, setNewOptionValue] = useState('')

  const update = (patch: Partial<LocalField>) => onChange({ ...field, ...patch })

  const setTranslation = (langIdx: number, patch: Partial<FieldTranslation>) => {
    const translations = field.translations.map((t, i) => (i === langIdx ? { ...t, ...patch } : t))
    update({ translations })
  }

  const addOption = () => {
    if (!newOptionValue.trim()) return
    const option: LocalOption = {
      value: newOptionValue.trim(),
      sortOrder: field.options.length,
      isCorrect: false,
      translations: makeEmptyOptionTranslations(languages),
    }
    update({ options: [...field.options, option] })
    setNewOptionValue('')
  }

  const updateOption = (optIdx: number, patch: Partial<LocalOption>) => {
    const options = field.options.map((o, i) => (i === optIdx ? { ...o, ...patch } : o))
    update({ options })
  }

  const setOptionTranslation = (optIdx: number, langId: number, label: string) => {
    const options = field.options.map((o, i) => {
      if (i !== optIdx) return o
      const translations = o.translations.map(t => (t.marketLanguageId === langId ? { ...t, label } : t))
      return { ...o, translations }
    })
    update({ options })
  }

  const removeOption = (optIdx: number) => {
    update({ options: field.options.filter((_, i) => i !== optIdx) })
  }

  const firstLabel = field.translations[0]?.label

  return (
    <Card variant="outlined" sx={{ mb: 2, opacity: field.isActive ? 1 : 0.5 }}>
      <CardContent>
        {/* Header row */}
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Chip label={FIELD_TYPE_LABELS[field.type]} size="small" color="primary" variant="outlined" />
            <Select
              value={field.formSection}
              onChange={e => update({ formSection: e.target.value as FieldSection })}
              size="small"
              sx={{ minWidth: 100, fontSize: 12 }}
            >
              {(Object.entries(FIELD_SECTION_LABELS) as [FieldSection, string][]).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
            <Typography fontWeight={600}>{firstLabel || <em style={{ color: '#aaa' }}>No label yet</em>}</Typography>
            {field.required && <Chip label="Required" size="small" color="warning" />}
            {!field.id && <Chip label="New" size="small" color="success" />}
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <IconButton size="small" onClick={onMoveUp} disabled={index === 0} title="Move up">
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={onMoveDown} disabled={index === total - 1} title="Move down">
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
            <FormControlLabel
              control={
                <Switch checked={field.required} onChange={e => update({ required: e.target.checked })} size="small" />
              }
              label="Required"
              sx={{ ml: 1 }}
            />
            <FormControlLabel
              control={
                <Switch checked={field.isActive} onChange={e => update({ isActive: e.target.checked })} size="small" />
              }
              label="Active"
            />
            <IconButton size="small" onClick={() => setExpanded(e => !e)}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
            <IconButton size="small" color="error" onClick={onDelete}>
              <TrashIcon />
            </IconButton>
          </Box>
        </Box>

        <Collapse in={expanded}>
          <Box mt={2}>
            {/* Translations */}
            <Typography variant="subtitle2" mb={1}>
              Translations
            </Typography>
            <Tabs value={langTab} onChange={(_, v) => setLangTab(v)} sx={{ mb: 2 }}>
              {languages.map((lang, i) => (
                <Tab key={lang.id} label={lang.nativeName || lang.name} value={i} />
              ))}
            </Tabs>
            {languages.map((_, i) => (
              <Box key={i} display={langTab === i ? 'block' : 'none'}>
                <Box display="flex" flexDirection="column" gap={1.5}>
                  <TextField
                    label="Label"
                    size="small"
                    fullWidth
                    value={field.translations[i]?.label ?? ''}
                    onChange={e => setTranslation(i, { label: e.target.value })}
                  />
                  <TextField
                    label="Placeholder"
                    size="small"
                    fullWidth
                    value={field.translations[i]?.placeholder ?? ''}
                    onChange={e => setTranslation(i, { placeholder: e.target.value })}
                  />
                  <TextField
                    label="Description"
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                    value={field.translations[i]?.description ?? ''}
                    onChange={e => setTranslation(i, { description: e.target.value })}
                  />
                </Box>
              </Box>
            ))}

            {/* Options */}
            {HAS_OPTIONS.includes(field.type) && (
              <Box mt={3}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" mb={1}>
                  Options
                </Typography>
                {field.options.map((opt, optIdx) => (
                  <Box key={optIdx} mb={2} p={1.5} border="1px solid" borderColor="divider" borderRadius={1}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Chip label={opt.value} size="small" />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={opt.isCorrect}
                            onChange={e => updateOption(optIdx, { isCorrect: e.target.checked })}
                            size="small"
                          />
                        }
                        label="Correct"
                      />
                      <IconButton size="small" color="error" onClick={() => removeOption(optIdx)}>
                        <TrashIcon />
                      </IconButton>
                    </Box>
                    <Box display="flex" flexDirection="column" gap={1}>
                      {languages.map(lang => (
                        <TextField
                          key={lang.id}
                          label={`Label (${lang.nativeName || lang.name})`}
                          size="small"
                          value={opt.translations.find(t => t.marketLanguageId === lang.id)?.label ?? ''}
                          onChange={e => setOptionTranslation(optIdx, lang.id, e.target.value)}
                        />
                      ))}
                    </Box>
                  </Box>
                ))}
                <Box display="flex" gap={1} mt={1}>
                  <TextField
                    label="New option value (internal key)"
                    size="small"
                    value={newOptionValue}
                    onChange={e => setNewOptionValue(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addOption()}
                  />
                  <Button variant="outlined" startIcon={<Add />} onClick={addOption}>
                    Add
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  )
}

// ── FormBuilder ───────────────────────────────────────────────────────────────

const FormBuilder = (): JSX.Element => {
  const { id: marketId } = useParams<{ id: string }>()
  const { t } = useTranslation()
  const { token, refreshToken } = useAuthContext()
  const { changeMessage } = useAlert()
  const [fields, setFields] = useState<LocalField[]>([])
  const [newFieldType, setNewFieldType] = useState<FieldType>('short_text')
  const [saving, setSaving] = useState(false)

  const { data: serverFields, isLoading } = useQueryGet<ApplicationField[]>({
    queryKey: [`form-builder-${marketId}`],
    endpoint: `markets/${marketId}/application-form/admin`,
  })

  const { data: languages, isLoading: loadingLanguages } = useQueryGet<MarketLanguage[]>({
    queryKey: [`market-languages-${marketId}`],
    endpoint: `market-languages/${marketId}`,
  })

  // Initialise local state from server on first load
  useEffect(() => {
    if (!Array.isArray(serverFields) || !Array.isArray(languages)) return
    setFields(
      serverFields
        .slice()
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(f => ({
          ...f,
          translations: languages.map(lang => {
            const ex = f.translations.find(t => t.marketLanguageId === lang.id)
            return {
              marketLanguageId: lang.id,
              label: ex?.label ?? '',
              placeholder: ex?.placeholder ?? '',
              description: ex?.description ?? '',
            }
          }),
          options: (f.options ?? []).map(opt => ({
            ...opt,
            translations: languages.map(lang => {
              const ex = opt.translations?.find(t => t.marketLanguageId === lang.id)
              return { marketLanguageId: lang.id, label: ex?.label ?? '' }
            }),
          })),
        }))
    )
  }, [serverFields, languages])

  const addField = () => {
    const newField: LocalField = {
      type: newFieldType,
      formSection: 'contest',
      required: false,
      sortOrder: fields.length,
      isActive: true,
      translations: makeEmptyTranslations(languages as MarketLanguage[]),
      options: [],
    }
    setFields(prev => [...prev, newField])
  }

  const deleteField = async (index: number) => {
    const field = fields[index]
    if (field.id) {
      await axiosDelete(token, refreshToken, `markets/${marketId}/application-form/fields/${field.id}`)
    }
    setFields(prev => prev.filter((_, i) => i !== index))
  }

  const moveField = (index: number, direction: 'up' | 'down') => {
    const swapIdx = direction === 'up' ? index - 1 : index + 1
    if (swapIdx < 0 || swapIdx >= fields.length) return
    setFields(prev => {
      const next = [...prev]
      ;[next[index], next[swapIdx]] = [next[swapIdx], next[index]]
      return next.map((f, i) => ({ ...f, sortOrder: i }))
    })
  }

  const save = async () => {
    if (!marketId) return
    setSaving(true)
    try {
      for (let i = 0; i < fields.length; i++) {
        const field = fields[i]
        const sortOrder = i

        // 1. Create or update field
        let fieldId = field.id
        if (!fieldId) {
          const created = await axiosPost<{ id: number }>(
            token,
            refreshToken,
            `markets/${marketId}/application-form/fields`,
            {
              type: field.type,
              formSection: field.formSection,
              required: field.required,
              sortOrder,
            }
          )
          if (!created || 'error' in created) throw new Error('Failed to create field')
          fieldId = created.id
        } else {
          await axiosPatch(token, refreshToken, `markets/${marketId}/application-form/fields/${fieldId}`, {
            formSection: field.formSection,
            required: field.required,
            isActive: field.isActive,
            sortOrder,
          })
        }

        // 2. Upsert field translations
        const nonEmptyTranslations = field.translations.filter(t => t.label.trim())
        if (nonEmptyTranslations.length) {
          await axiosPost(
            token,
            refreshToken,
            `markets/${marketId}/application-form/fields/${fieldId}/translations`,
            nonEmptyTranslations
          )
        }

        // 3. Create or update options
        for (let j = 0; j < field.options.length; j++) {
          const opt = field.options[j]
          let optionId = opt.id

          if (!optionId) {
            const created = await axiosPost<{ id: number }>(
              token,
              refreshToken,
              `markets/${marketId}/application-form/fields/${fieldId}/options`,
              { value: opt.value, sortOrder: j, isCorrect: opt.isCorrect }
            )
            if (!created || 'error' in created) throw new Error('Failed to create option')
            optionId = created.id
          } else {
            await axiosPatch(
              token,
              refreshToken,
              `markets/${marketId}/application-form/fields/${fieldId}/options/${optionId}`,
              {
                isCorrect: opt.isCorrect,
                sortOrder: j,
              }
            )
          }

          // 4. Upsert option translations
          const nonEmptyOptTranslations = opt.translations.filter(t => t.label.trim())
          if (nonEmptyOptTranslations.length) {
            await axiosPost(
              token,
              refreshToken,
              `markets/${marketId}/application-form/fields/${fieldId}/options/${optionId}/translations`,
              nonEmptyOptTranslations
            )
          }
        }
      }

      changeMessage('Form saved successfully', 'success', () => {})
      // Reload server state so IDs are up to date
      window.location.reload()
    } catch {
      changeMessage('Error saving form', 'error', () => {})
    } finally {
      setSaving(false)
    }
  }

  if (isLoading || loadingLanguages) return <RequestStatus data={undefined} isLoading />
  if (!Array.isArray(languages)) return <RequestStatus data={{ error: undefined }} isLoading={false} />

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={600} component="h1">
          {t('navigation.formBuilder')}
        </Typography>
        <Button
          variant="outlined"
          color="secondary"
          onClick={() =>
            axiosPost(token, refreshToken, `markets/${marketId}/application-form/versions`, {}).then(() =>
              changeMessage('Version snapshot created', 'success', () => {})
            )
          }
        >
          Save version snapshot
        </Button>
      </Box>

      {/* Add field row */}
      <Box mb={3} display="flex" gap={2} alignItems="center">
        <Select
          value={newFieldType}
          onChange={e => setNewFieldType(e.target.value as FieldType)}
          size="small"
          sx={{ minWidth: 200 }}
        >
          {(Object.entries(FIELD_TYPE_LABELS) as [FieldType, string][]).map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
        <Button variant="outlined" startIcon={<Add />} onClick={addField}>
          Add field
        </Button>
      </Box>

      {/* Field list */}
      {fields.length === 0 && <Typography color="text.secondary">No fields yet. Add the first field above.</Typography>}

      {fields.map((field, index) => (
        <FieldCard
          key={field.id ?? `new-${index}`}
          field={field}
          index={index}
          total={fields.length}
          languages={languages}
          onChange={updated => setFields(prev => prev.map((f, i) => (i === index ? updated : f)))}
          onDelete={() => deleteField(index)}
          onMoveUp={() => moveField(index, 'up')}
          onMoveDown={() => moveField(index, 'down')}
        />
      ))}

      {/* Save button */}
      {fields.length > 0 && (
        <Box mt={3} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            size="large"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <DoneOutlinedIcon />}
            onClick={save}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save form'}
          </Button>
        </Box>
      )}
    </>
  )
}

export default FormBuilder
